/**
 * McpTokensPanel (PRP-MCP-10 work item 4): personal access tokens and the Claude
 * Code setup command. Lives under "For developers" on /assistant; most people
 * connect through the chat app's own sign-in and never need a token.
 */
import { useCallback, useEffect, useState } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { McpCreatedToken, McpToken } from '../../lib/mcpApi';
import type { ApiError } from '../../types';
import { Banner } from './ui/banner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Pill } from './ui/pill';
import { Select } from './ui/select';
import CopyField from './McpCopyField';

/** Expiry choices for a new token, in days; 0 means no expiry. */
const EXPIRY_CHOICES: { days: number; label: string }[] = [
  { days: 0, label: 'Never expires' },
  { days: 30, label: 'Expires in 30 days' },
  { days: 90, label: 'Expires in 90 days' },
  { days: 365, label: 'Expires in 1 year' },
];

/** True when an ISO timestamp is in the past. */
function isPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t <= Date.now();
}

/** Formats an ISO timestamp as a short local date, or a dash. */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
}

/** Token list, creation, revocation and the Claude Code command. */
export default function McpTokensPanel({ serverUrl }: { serverUrl: string }) {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [name, setName] = useState('');
  const [expiryDays, setExpiryDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<McpCreatedToken | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    const res = await McpAPI.listTokens();
    setTokens(res.tokens ?? []);
  }, []);

  useEffect(() => {
    loadTokens().catch((err: ApiError) =>
      setError(err?.message || 'Could not load your tokens.'),
    );
  }, [loadTokens]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give the token a name, for example "Claude on my laptop".');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = expiryDays
        ? await McpAPI.createToken(trimmed, expiryDays)
        : await McpAPI.createToken(trimmed);
      setCreated(res);
      setName('');
      setExpiryDays(0);
      await loadTokens();
    } catch (err) {
      setError((err as ApiError).message || 'Could not create the token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await McpAPI.revokeToken(id);
      if (created?.id === id) setCreated(null);
      await loadTokens();
    } catch (err) {
      setError((err as ApiError).message || 'Could not revoke the token.');
    } finally {
      setBusy(false);
    }
  };

  const command = `claude mcp add --transport http aiea ${serverUrl || '<server_url>'} --header "Authorization: Bearer ${created?.token ?? '<token>'}"`;

  return (
    <div className="space-y-5" data-testid="mcp-tokens-panel">
      {error && <Banner tone="warn">{error}</Banner>}
      <div>
        <div className="mb-1.5 text-[12px] font-[550] text-[var(--tx-mid)]">Access tokens</div>
        {tokens.length === 0 ? (
          <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">You have no tokens yet.</p>
        ) : (
          <ul className="m-0 p-0 list-none divide-y divide-[var(--bd-subtle)]" aria-label="Access tokens">
            {tokens.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 py-2 text-[12.5px]">
                <div className="flex-1 min-w-0">
                  <div className="text-[var(--tx-hi)] font-[550] truncate">{t.name}</div>
                  <div className="text-[11.5px] text-[var(--tx-lo)]">
                    <span className="font-[var(--font-mono)]">{t.prefix}</span>
                    {' · created '}
                    {fmtDate(t.created_at)}
                    {' · last used '}
                    {t.last_used_at ? fmtDate(t.last_used_at) : 'never'}
                    {t.expires_at && !isPast(t.expires_at) ? ` · expires ${fmtDate(t.expires_at)}` : ''}
                  </div>
                </div>
                {t.revoked_at ? (
                  <Pill kind="neutral">{`Revoked ${fmtDate(t.revoked_at)}`}</Pill>
                ) : isPast(t.expires_at) ? (
                  <Pill kind="neutral">{`Expired ${fmtDate(t.expires_at)}`}</Pill>
                ) : (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => void revoke(t.id)}
                    aria-label={`Revoke ${t.name}`}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Token name, for example Claude on my laptop"
          aria-label="Token name"
          disabled={busy}
          className="min-w-0 flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void create();
          }}
        />
        <Select
          value={String(expiryDays)}
          onChange={(e) => setExpiryDays(Number(e.target.value))}
          aria-label="Token expiry"
          disabled={busy}
          className="w-auto shrink-0"
        >
          {EXPIRY_CHOICES.map((c) => (
            <option key={c.days} value={c.days}>
              {c.label}
            </option>
          ))}
        </Select>
        <Button variant="primary" onClick={() => void create()} disabled={busy}>
          Create token
        </Button>
      </div>

      {created && (
        <div
          data-testid="mcp-new-token"
          className="space-y-2 p-3 rounded-[var(--r-md)] border border-[var(--accent-line)] bg-[var(--accent-soft)]"
        >
          <p className="m-0 text-[12.5px] font-[550] text-[var(--tx-hi)]">
            Copy this token now. You will not see it again.
          </p>
          <CopyField label="new token" value={created.token} />
          <Button size="sm" variant="ghost" onClick={() => setCreated(null)}>
            I copied it
          </Button>
        </div>
      )}

      <div className="space-y-2 text-[12.5px] text-[var(--tx-mid)]">
        <div className="text-[12px] font-[550]">Set up Claude Code</div>
        <p className="m-0">
          Create a token above, then run this command in your terminal. Replace the token part
          if you did not just create one.
        </p>
        <CopyField label="setup command" value={command} />
      </div>
    </div>
  );
}
